import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useActionRequest, useDataRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import {
  ConnectedSystemAutomationCallout,
  isAwaitingConnectedSystemAutomation,
  useConnectedSystemAutomationBlock,
} from "../shared/ConnectedSystemAutomationCallout";
import { NestedVendorList } from "../shared/NestedVendorList";
import { CategoryHeader } from "../shared/CategoryHeader";
import { AUTOMATION_OUTCOMES, AutomationOutcomeRow } from "../shared/AutomationOutcomeRow";
import { InstructionsContent } from "../shared/CollapsibleInstructions";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  getRequestProcessingRegion,
  isFacebookAds,
  isRequestContextApplicableWithCrunchFiltering,
  isShopify,
  isShopifyAudiences,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { DIRECT_MAIL_VENDORS_SLUG } from "../../../../../surveys/sharingSurvey";
import clsx from "clsx";
import { parseMultiselectAnswerWithNoneOption } from "../../../../../surveys/survey";
import { useSnackBar } from "@truevault/aquilo";
import { DataRequestCustomFieldDisplay } from "../../DataRequestCustomFieldDisplay";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import {
  isOptOutWorkflowVendorType,
  isVendorAllowedInOptOutWorkflow,
} from "../../../../../util/optOutWorkflowVendors";
import { getVendorNesting } from "../../../../../util/vendorNesting";

const getAutomationCallout = (automationVendorResult: string) => {
  switch (automationVendorResult) {
    case "AUTOMATION_REQUESTED":
      return (
        <Callout
          variant={CalloutVariant.Green}
          title="Request to remove automatically sent"
        ></Callout>
      );
    case "AUTOMATION_CONFIRMED":
      return (
        <Callout variant={CalloutVariant.Green} title="Consumer automatically removed"></Callout>
      );
    case "AUTOMATION_NO_DATA":
      return (
        <Callout
          variant={CalloutVariant.Secondary}
          title="Vendor indicated no data to remove for the consumer"
        ></Callout>
      );
    default:
      return null;
  }
};

export const RemoveConsumer = () => {
  const [org] = usePrimaryOrganization();

  const [allVendors] = useOrganizationVendors(org.id, undefined, true);
  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();
  const [vendorOutcomes, setVendorOutcomes] = useState<Record<string, string>>({});

  const isBatch = !!request.batch;

  const [isPerformingVendorUpdate, setIsPerformingVendorUpdate] = useState<boolean>(false);

  const snackbar = useSnackBar();
  const enqueueSnackbarElevated = useCallback(
    (message, options) => {
      snackbar(message, options);
    },
    [snackbar],
  );

  const [bulkProgress, setBulkProgress] = useState(0); // How many vendors have we gotten through updating for the bulk workflow?
  const numBulkRequests = request?.batch?.requestPublicIds?.length ?? 0; // How many vendors total are there for the bulk workflow
  const bulkProgressFormatted =
    bulkProgress > 0 && numBulkRequests > 0 ? `(${bulkProgress} of ${numBulkRequests})` : null; // Formatting for displaying the progress of the bulk workflow
  const [vendorIdInProgress, setVendorIdInProgress] = useState(null); // Which vendor is being updated (so we know for which vendor to show the loading spinner)

  const { result: fetchedInstructions } = useDataRequest({
    queryKey: ["fetchedOptOutInstructions", org.id],
    api: () => Api.requestHandlingInstructions.getOptOutInstructions(org.id),
  });
  // This does not match where the data is stored based on the OptOutInstructionPage
  // It may very well be that we want to update the OptOutInstructionPage Page instead
  // const [fetchedInstructions] = useRequestInstructions(org.id, workflowType);

  const { fetch: updateVendorOutcome } = useActionRequest({
    api: async ({ values }) => {
      setVendorIdInProgress(values.vendor);

      let requests: string[] = [];
      if (isBatch) {
        requests = request.batch.requestPublicIds;
        if (requests.length > 25) {
          enqueueSnackbarElevated(
            `Updating ${requests.length} requests may take a few moments, please wait...`,
            { variant: "info" },
          );
        }
      } else {
        requests = [request.id];
      }

      for (let i = 0; i < requests.length; i++) {
        const resp = await Api.dataRequest.updateVendor(org.id, requests[i], values);
        setBulkProgress(i);
        if (!resp.id) {
          // Don't throw here since the other requests need to finish still
          console.error(`invalid response from server for request ${requests[i]}`);
        }
      }

      if (isBatch) {
        // Some visual feedback for the batch workflow that the user is affecting a group of requests
        enqueueSnackbarElevated(`Saved update for ${request.batch.requestCount} requests`, {
          variant: "success",
        });
      }

      setVendorIdInProgress(null);
    },
    onError: (networkRequestState) => {
      if (networkRequestState.errorMessage === undefined) {
        networkRequestState.errorMessage =
          "There was a problem saving your selection. Please try again.";
      }
      setVendorIdInProgress(null);
    },
  });

  const onSelectionChange = async (vendor, value) => {
    try {
      setIsPerformingVendorUpdate(true);

      await updateVendorOutcome({
        values: { vendor: vendor.vendorId, outcome: value },
      });
      setVendorOutcomes((prev) => Object.assign({}, prev, { [vendor.vendorId]: value }));
    } catch (e) {
      console.error("Error updating vendor outcome:", e);
    } finally {
      // Always reset state to ensure checkboxes are re-enabled
      setIsPerformingVendorUpdate(false);
      refreshRequest();
    }
  };

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { data: dataMap } = useDataMap(org.id);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  const requestProcessingRegion = getRequestProcessingRegion(request);

  const directMailVendorIds: string[] = useMemo(
    () => parseMultiselectAnswerWithNoneOption(answers, DIRECT_MAIL_VENDORS_SLUG),
    [answers],
  );

  /**
   * Whether a recipient belongs in this step on its own merits, ignoring vendor nesting. This is also the test for
   * nesting a recipient under a manager here, so the step only lists recipients it would have shown itself - a
   * delete-only recipient nested under the same manager stays out of the opt-out step (POL-7958).
   */
  const belongsInRemoveConsumerStep = useCallback(
    (v: OrganizationDataRecipientDto) => {
      if (!isVendorAllowedInOptOutWorkflow(v)) {
        return false;
      }

      if (!v.processingRegions.includes(requestProcessingRegion)) {
        return false;
      }
      // Ignore inaccessible vendors
      if (v.dataAccessibility == "NOT_ACCESSIBLE") {
        return false;
      }

      if (
        !isRequestContextApplicableWithCrunchFiltering(dataMap, request.context, v, request, org)
      ) {
        return false;
      }

      return isOptOutWorkflowVendorType(v, directMailVendorIds);
    },
    [dataMap, directMailVendorIds, org, request, requestProcessingRegion],
  );

  const nesting = useMemo(
    () =>
      getVendorNesting(allVendors, managedAdNetworks, answers, {
        isNestedVendorRelevant: belongsInRemoveConsumerStep,
      }),
    [allVendors, managedAdNetworks, answers, belongsInRemoveConsumerStep],
  );
  const {
    activeManagers: managementVendors,
    getManagerForManagedVendor: getManagementVendorForNetwork,
    getManagedVendorsForManager,
  } = nesting;

  const vendors = useMemo(() => {
    if (!allVendors) {
      return undefined;
    }

    const result = allVendors.filter(
      // Vendors nested under a manager are handled there, so they are not listed separately.
      (v) => !nesting.isManaged(v.id) && belongsInRemoveConsumerStep(v),
    );

    // Managers stand in for the recipients they handle, so a manager that manages anything shown in this step gets a
    // row even when it would not qualify on its own (the Connected System never does - it is not an upload vendor).
    const managementVendorsToAdd = managementVendors.filter(
      (vendor) =>
        !result.find((v) => v.id === vendor.id) && vendor.dataAccessibility !== "NOT_ACCESSIBLE",
    );

    return result.concat(managementVendorsToAdd);
  }, [allVendors, belongsInRemoveConsumerStep, managementVendors, nesting]);

  useEffect(() => {
    const vendorOutcomes = request.vendorOutcomes.reduce((acc, outcome) => {
      return Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome });
    }, {});

    // Only update if there are actual changes to prevent infinite loops
    setVendorOutcomes((prev) => {
      const hasChanges =
        Object.keys(vendorOutcomes).some((key) => vendorOutcomes[key] !== prev[key]) ||
        Object.keys(prev).length !== Object.keys(vendorOutcomes).length;

      return hasChanges ? vendorOutcomes : prev;
    });
  }, [request]);

  useEffect(() => {
    const outcomes = request.vendorOutcomes;
    const everyOutcomeCompleted = vendors?.every((vendor) => {
      const outcome = outcomes?.find((o) => o.vendor.id == vendor.vendorId);
      return outcome?.outcome != null && outcome?.outcome !== "AUTOMATION_FAILED";
    });

    setStepCompleted(
      vendors &&
        vendors.length <= outcomes.length &&
        everyOutcomeCompleted &&
        !isPerformingVendorUpdate,
    );
  }, [request, setStepCompleted, vendors, isPerformingVendorUpdate]);

  const sortedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors ?? []);
  }, [vendors]);

  // The Connected System row has no manual controls, so the step waits on its automation (POL-7958).
  const stepAwaitsConnectedSystem = (vendors ?? []).some((vendor) =>
    isAwaitingConnectedSystemAutomation(vendor, vendorOutcomes[vendor.vendorId], request),
  );
  useConnectedSystemAutomationBlock(stepAwaitsConnectedSystem, request.allowManualAutomation);

  // Automation outcomes recorded on the request whose vendor is not shown as an outcome row above
  // (e.g. a vendor that has been filtered out of the vendor list). These are surfaced in a separate
  // "Automations" section so the outcome isn't silently dropped.
  const automationOnlyOutcomes = useMemo(() => {
    const shownVendorIds = new Set(sortedVendors.map((v) => v.vendorId));
    // The Shopify automation outcome is already represented by the Shopify Network Intelligence
    // row when SNI is shown, so don't repeat it as a separate Shopify row.
    const sniIsShown = sortedVendors.some(isShopifyAudiences);
    return request.vendorOutcomes.filter(
      (outcome) =>
        AUTOMATION_OUTCOMES.includes(outcome.outcome) &&
        !shownVendorIds.has(outcome.vendor.id) &&
        !(sniIsShown && isShopify(outcome.vendor)),
    );
  }, [request.vendorOutcomes, sortedVendors]);

  return (
    <>
      <div className="text-t3 mb-std">
        Remove the consumer from any data sharing and selling processes or lists involving the Data
        Recipients below. Your defaults and instructions can be updated in{" "}
        <Link className="text-weight-regular" to="/organization/requestHandling">
          Request Instructions.
        </Link>
      </div>

      {Boolean(fetchedInstructions) && (
        <Callout variant={CalloutVariant.Clear} className={"mb-std"}>
          <p>{fetchedInstructions}</p>
        </Callout>
      )}

      <DataRequestCustomFieldDisplay
        org={org}
        request={request}
        classList={fetchedInstructions ? "mb-std" : "mb-md"}
      />

      {sortedVendors && (
        <CategorizedVendorList
          vendors={sortedVendors}
          renderVendor={(vendor) => {
            const optOutInstruction = vendor.instructions.find((vi) => vi.requestType == "OPT_OUT");
            const resolutionValue = vendorOutcomes[vendor.vendorId] || ""; // Need to always have some value here to prevent MUI from treating `value` as uncontrolled

            const resolvedAutomatically = [
              "AUTOMATION_REQUESTED",
              "AUTOMATION_CONFIRMED",
              "AUTOMATION_NO_DATA",
            ].includes(resolutionValue);
            const reconnectNudge = (
              <>
                Try disconnecting and reconnecting to this vendor on the{" "}
                <Link className="text-weight-regular" to="/privacy-center/integrations">
                  Integrations page
                </Link>
                , then press the Automate button to retry.
              </>
            );
            const metaAdsTOSReminder = (
              <>
                For Meta Ads, be sure you've logged in to the Meta Business Account at least once
                and accepted the Custom Audience Terms of Service.
              </>
            );
            const outcomeDto = request.vendorOutcomes.find(
              (vo) => vo.vendor.id === vendor.vendorId,
            );

            const isAutomationFailed =
              outcomeDto?.automationVendorTaskFailedMessage != null ||
              resolutionValue === "AUTOMATION_FAILED";
            const labelText = isAutomationFailed ? "Removed manually" : "Completed";

            // The Connected System is resolved by automation, so it gets an explanation instead of the
            // manual "Completed" checkbox (POL-7958).
            const awaitingConnectedSystem = isAwaitingConnectedSystemAutomation(
              vendor,
              resolutionValue,
              request,
            );

            const rowContent = resolvedAutomatically ? (
              getAutomationCallout(resolutionValue)
            ) : awaitingConnectedSystem ? (
              <ConnectedSystemAutomationCallout />
            ) : (
              <>
                {resolutionValue === "AUTOMATION_FAILED" && (
                  <Callout
                    variant={CalloutVariant.Yellow}
                    title="Automatic removal not successful"
                    className="mb-sm"
                    subtitle={
                      isFacebookAds(vendor) ? (
                        <>
                          {reconnectNudge} {metaAdsTOSReminder}
                        </>
                      ) : (
                        <>{reconnectNudge}</>
                      )
                    }
                  ></Callout>
                )}
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        aria-label="Vendor handling response"
                        name={`vendor-${vendor.id}-data-handling`}
                        onChange={(e) => {
                          onSelectionChange(vendor, e.target.checked ? "COMPLETE" : null);
                        }}
                        checked={resolutionValue === "COMPLETE"}
                        disabled={isPerformingVendorUpdate}
                      />
                    }
                    label={
                      isPerformingVendorUpdate &&
                      vendorIdInProgress == vendor.vendorId &&
                      numBulkRequests > 1 ? (
                        <div className="flex-row">
                          <span>{labelText}</span>
                          <CircularProgress
                            color="primary"
                            size="1rem"
                            className="ml-xs"
                            sx={{ marginTop: "4px" }}
                          />
                          <Typography variant="subtitle2">{bulkProgressFormatted}</Typography>
                        </div>
                      ) : (
                        labelText
                      )
                    }
                  />
                </FormControl>
              </>
            );

            const nestedVendorList = (
              <NestedVendorList
                managerName={vendor.name}
                nestedVendors={getManagedVendorsForManager(vendor.id)}
              />
            );
            // When the outcome is a lone callout, the vendor name is centered on it however many lines either wraps
            // to. The nested list wraps onto a second line so it never stretches the line the two share (POL-7992).
            const outcomeIsCallout = resolvedAutomatically || awaitingConnectedSystem;

            return (
              <div key={`vendor-${vendor.id}`} className={"vendor-data-container"}>
                <hr />
                <div
                  className={clsx(
                    "flex-container pt-std pb-std border-top--grey vendor-data-card",
                    { "flex--wrap flex--align-center": outcomeIsCallout },
                  )}
                >
                  <div className="flex-grow vendor-data-vanity">
                    <div className="text-t4 w-256 flex-container flex--align-center pl-xs">
                      <DataRecipientLogo dataRecipient={vendor} size={32} />
                      <div className="ml-mdlg" style={{ width: "95px" }}>
                        {vendor.name}
                        {nesting.isManaged(vendor.id) && (
                          <span className="text-neutral-600 ml-xs flex-row">
                            (managed by {getManagementVendorForNetwork(vendor.id)?.name})
                          </span>
                        )}
                      </div>
                      {resolvedAutomatically && (
                        <img
                          src="/assets/images/automation-icon.svg"
                          alt="Resolved automatically"
                          className="ml-xs"
                        />
                      )}
                    </div>
                    {!outcomeIsCallout && nestedVendorList}
                  </div>
                  <div className={outcomeIsCallout ? "flex--expand-self" : "flex-grow"}>
                    {rowContent}

                    {!resolvedAutomatically &&
                      !awaitingConnectedSystem &&
                      (() => {
                        const isManaged = nesting.isManaged(vendor.id);
                        const manager = isManaged ? getManagementVendorForNetwork(vendor.id) : null;
                        const instructionsToShow =
                          isManaged && manager
                            ? manager.optOutInstructions
                            : vendor.optOutInstructions;

                        if (!instructionsToShow && !isManaged) return null;

                        return (
                          <Callout variant={CalloutVariant.Gray} className={"my-md"}>
                            <div className="text-weight-bold">
                              {isManaged && manager
                                ? `Opt-Out Instructions through ${manager.name}`
                                : `Opt-Out Instructions`}
                            </div>
                            <div
                              className="text-t2"
                              dangerouslySetInnerHTML={{
                                __html:
                                  instructionsToShow ||
                                  "We have not found opt-out instructions for this vendor. Contact the vendor for instructions on how to remove the consumer from a custom audience list.",
                              }}
                            ></div>
                          </Callout>
                        );
                      })()}

                    {!resolvedAutomatically && (
                      <InstructionsContent
                        processingInstructions={optOutInstruction?.processingInstructions}
                        title="Opt-Out Instructions"
                        isExpanded={true}
                      />
                    )}
                  </div>
                  {outcomeIsCallout && (
                    <div className="w-100">
                      <div className="vendor-data-vanity">{nestedVendorList}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        />
      )}

      {automationOnlyOutcomes.length > 0 && (
        <>
          <CategoryHeader categoryName="Automations" showVendorsSuffix={false} />

          {automationOnlyOutcomes.map((outcome) => (
            <AutomationOutcomeRow
              key={outcome.vendor.id}
              vendor={outcome.vendor}
              outcome={outcome.outcome}
              variant="opt-out"
              nestedVendors={getManagedVendorsForManager(
                allVendors?.find((v) => v.vendorId === outcome.vendor.id)?.id,
              )}
            />
          ))}
        </>
      )}
    </>
  );
};
